import React, { useEffect, useState } from 'react';
import { Card, Typography, List, Tag, Space, Skeleton, Button, Avatar, Empty, Progress } from 'antd';
import { 
  UserOutlined,
  MailOutlined,
  CalendarOutlined,
  BellOutlined
} from '@ant-design/icons';
import { dashboardService, ExpiringSubscription } from '../../services/dashboardService';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount) + ' FCFA';
};

const getDaysColor = (days: number): string => {
  if (days <= 1) return '#ff4d4f';
  if (days <= 3) return '#fa8c16';
  return '#1890ff';
};

const getDaysProgress = (days: number): number => {
  // Invert: fewer days = more urgent (higher percentage)
  return Math.max(0, Math.min(100, ((7 - days) / 7) * 100));
};

const ExpiringSubscriptionsWidget: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<ExpiringSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await dashboardService.getExpiringSubscriptions(7, 5);
        setSubscriptions(data);
      } catch (err) {
        console.error('Erreur chargement abonnements expirants:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Card style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Skeleton active avatar paragraph={{ rows: 3 }} />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <BellOutlined style={{ color: '#ff4d4f' }} />
          <span>Abonnements Expirants</span>
          {subscriptions.length > 0 && (
            <Tag color="red">{subscriptions.length}</Tag>
          )}
        </Space>
      }
      extra={
        <Text type="secondary">Prochains 7 jours</Text>
      }
      style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
    >
      {subscriptions.length === 0 ? (
        <Empty 
          description="Aucun abonnement n'expire bientôt"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <>
          <List
            itemLayout="horizontal"
            dataSource={subscriptions}
            renderItem={(item) => (
              <List.Item>
                <List.Item.Meta
                  avatar={
                    <Avatar 
                      style={{ backgroundColor: getDaysColor(item.days_remaining) }}
                      icon={<UserOutlined />}
                    />
                  }
                  title={
                    <Space direction="vertical" size={0} style={{ width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong>{item.customer?.name || 'Client inconnu'}</Text>
                        <Tag color={getDaysColor(item.days_remaining)}>
                          {item.days_remaining === 0 
                            ? "Aujourd'hui" 
                            : item.days_remaining === 1 
                              ? "Demain" 
                              : `${item.days_remaining} jours`}
                        </Tag>
                      </div>
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                      <Space>
                        <Tag color="blue">{item.offer_name}</Tag>
                        <Text type="secondary">{formatCurrency(item.price)}</Text>
                      </Space>
                      <Space split={<Text type="secondary">•</Text>}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          <CalendarOutlined /> Fin: {formatDate(item.end_date)}
                        </Text>
                        {item.customer?.email && (
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            <MailOutlined /> {item.customer.email}
                          </Text>
                        )}
                      </Space>
                      <Progress 
                        percent={getDaysProgress(item.days_remaining)} 
                        showInfo={false}
                        strokeColor={getDaysColor(item.days_remaining)}
                        size="small"
                        style={{ marginTop: 4 }}
                      />
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button type="link" onClick={() => navigate('/maintenance-offers')}>
              Voir tous les abonnements expirants
            </Button>
          </div>
        </>
      )}
    </Card>
  );
};

export default ExpiringSubscriptionsWidget;
